import Palette from 'DesignSystem2/Theme/Palette';

import { COMPUTE_CHIPS, type ComputeChip, DEFAULT_COMPUTE_CHIP } from './computeForwardCurveApi';

/**
 * Types, colors, and series-ticker helpers for the Kalshi Compute forward curve
 * (GPU price markets). The curve itself is computed from live market ladders in
 * useComputeForwardCurve.
 *
 * Two contract families:
 *   - Weekly settle (KX{chip}WS): 6 weekly "Above $X" markets, rolling 6 weeks
 *     out, each settling Friday.
 *   - Monthly average (KX{chip}MS): monthly period-average markets out ~12
 *     months.
 *
 * Each contract's forward is the probability-weighted average of its strike
 * ladder (see computeForwardCurveMath).
 */

export interface ComputeCurvePoint {
    date: Date;
    /** Forward price, $/GPU-hr. */
    value: number;
    /** Short label for a contract node (e.g. W26, or a month). */
    label?: string;
    /** The contract's event ticker, for linking to its market page. */
    eventTicker?: string;
}

export interface ComputeCurve {
    chipLabel: string;
    /**
     * Daily realized ORNN spot (the 24h mean the weeklies settle on), oldest
     * first — the historical line to the left of `now`. Sourced from the ORNN
     * export and extended by any weekly that settled after it.
     */
    realized: ComputeCurvePoint[];
    /**
     * Already-settled weekly contracts, marked as points on the realized line.
     * Their `expiration_value` equals the realized series by construction.
     */
    settled: ComputeCurvePoint[];
    weekly: ComputeCurvePoint[];
    monthly: ComputeCurvePoint[];
    /** Where weekly settles end and monthly contracts begin. */
    boundary: Date;
    /** Where past becomes present: today, clamped between the last realized and first forward point. */
    now: Date;
}

export const COMPUTE_LINE_COLOR = Palette.Green.x10;

/** Price display, $/GPU-hr. Shared so the site chart and the broadcast frame agree. */
export const formatComputePrice = (value: number): string => `$${value.toFixed(2)}`;

export interface ComputeMonthToDate {
    /** Mean of the daily closes in the current month, $/GPU-hr. */
    average: number;
    /** The month covered, "YYYY-MM". */
    month: string;
    /** Daily closes in the average. */
    days: number;
}

const DAY_MS = 86_400_000;
/**
 * If the latest daily close is older than this, the realized series isn't current, so there
 * is no live month-to-date. ORNN publishes daily (weekends included), so a live chip is never
 * more than ~a day stale; this catches a chip with no live feed (H100 / A100), whose realized
 * series is the frozen archive that would otherwise yield a stale average mislabeled as live.
 */
const MONTH_TO_DATE_MAX_STALE_DAYS = 4;

/**
 * Month-to-date average of the realized daily close: the mean of every point in the month
 * of the latest one. Computed from the realized series the chart already holds. Dates carry
 * a 4pm-ET (20:00Z) stamp, so the UTC month is the trading day's month with no ambiguity.
 * Null when the latest month has no points, or when the latest point is stale enough that
 * the average would no longer be "to date".
 */
export function computeMonthToDate(realized: ComputeCurvePoint[], nowMs: number): ComputeMonthToDate | null {
    const latest = realized.at(-1);
    if (!latest) return null;
    if (nowMs - latest.date.getTime() > MONTH_TO_DATE_MAX_STALE_DAYS * DAY_MS) return null;
    const year = latest.date.getUTCFullYear();
    const month = latest.date.getUTCMonth();
    const inMonth = realized.filter((p) => p.date.getUTCFullYear() === year && p.date.getUTCMonth() === month);
    if (inMonth.length === 0) return null;
    return {
        average: inMonth.reduce((sum, p) => sum + p.value, 0) / inMonth.length,
        month: `${year}-${String(month + 1).padStart(2, '0')}`,
        days: inMonth.length,
    };
}

/**
 * Chips whose display name isn't just the key. Everything else falls through to the key,
 * so a new chip only ever needs adding to COMPUTE_CHIPS.
 */
const CHIP_LABEL_OVERRIDES: Partial<Record<ComputeChip, string>> = {
    RTX5090: 'RTX 5090',
};

// Built from COMPUTE_CHIPS rather than written out again: three hand-maintained copies of
// the chip list (the enum, the labels, this pattern) meant a chip added to one and missed
// in another would silently fall through to the default below and render another chip's
// curve under the new chip's name.
const COMPUTE_SERIES_PATTERN = new RegExp(`^KX(${COMPUTE_CHIPS.join('|')})(WS|MS)$`);

/** Map a compute series ticker (KXB200WS weekly / KXB200MS monthly) to its chip key. */
export function chipKeyFromSeriesTicker(seriesTicker: string | undefined): ComputeChip {
    const match = COMPUTE_SERIES_PATTERN.exec(seriesTicker?.toUpperCase() ?? '');
    return (match?.[1] as ComputeChip | undefined) ?? DEFAULT_COMPUTE_CHIP;
}

/** True for a compute forward-curve series (KX{chip}WS / KX{chip}MS). */
export function isComputeSeriesTicker(seriesTicker: string | undefined): boolean {
    return COMPUTE_SERIES_PATTERN.test(seriesTicker?.toUpperCase() ?? '');
}

/** True for a monthly-average compute series (KX{chip}MS), which settles on the month average. */
export function isMonthlyComputeSeries(seriesTicker: string | undefined): boolean {
    return COMPUTE_SERIES_PATTERN.exec(seriesTicker?.toUpperCase() ?? '')?.[2] === 'MS';
}

/** Display label for a chip key (e.g. RTX5090 -> "RTX 5090"). */
export function chipLabelFromKey(chipKey: string): string {
    return CHIP_LABEL_OVERRIDES[chipKey as ComputeChip] ?? chipKey;
}
